import type {
  CustomerOrder,
  CustomerOrderStatus,
  OrderFulfillment,
} from "../../api/types";
import type { TranslateFn } from "../../i18n/t";
import type { PlainKey } from "../../i18n/types";
import { localDayKey } from "../../utils/dayKey";
import { timeText } from "../../i18n/intl";

/** @see localDayKey — one implementation, shared with tasks and orders. */
export const orderDayKey = localDayKey;

/**
 * The calendar day an order belongs to. Prefer the precise slot start; fall
 * back to the scheduledDate bucket (UTC midnight, same local day in Madrid).
 */
export function orderDayDate(order: CustomerOrder): Date {
  return new Date(order.scheduledFor ?? order.scheduledDate);
}

/**
 * G-112 review fix: status words through the dictionary, so the kiosk's
 * order card speaks the florist's language (CRM pages still read English —
 * `useT` is "en" outside the kiosk). Takes `t` like `fulfillmentLabel`, for
 * the repaint on a language switch; the key keeps the Prisma code verbatim.
 */
export function orderStatusLabel(
  t: TranslateFn,
  status: CustomerOrderStatus,
): string {
  return t(`orders.status.${status}` as PlainKey);
}

export function orderStatusClass(status: CustomerOrderStatus): string {
  switch (status) {
    case "NEW":
      return "badge muted";
    case "CONFIRMED":
      return "badge primary";
    case "IN_PREPARATION":
      return "badge warning";
    case "READY":
      return "badge success";
    case "COMPLETED":
      return "badge success";
    case "CANCELLED":
      return "badge danger";
  }
}

/**
 * True when the delivery is priced and scheduled but nobody is carrying it yet
 * (`DeliveryJob.status === "PENDING"`). This is derived, never stored: PENDING
 * already *is* the "needs a courier" state, so a separate flag column could
 * only drift out of sync with the job it describes.
 */
export function orderNeedsCourier(order: CustomerOrder): boolean {
  if (orderFinished(order)) return false;
  return order.deliveryJob?.status === "PENDING";
}

/** Courier order that has no delivery job at all — not even priced yet. */
export function orderNotDispatched(order: CustomerOrder): boolean {
  if (orderFinished(order)) return false;
  if (order.fulfillment !== "COURIER" && order.fulfillment !== "EXTERNAL_COURIER") {
    return false;
  }
  return !order.deliveryJob;
}

/**
 * Done for good: cancelled, or completed and — for a web order, which "Hand
 * to delivery" completes before it leaves — carried out (G-166; the server's
 * one rule, `webAwaitingCourier`).
 */
function orderFinished(order: CustomerOrder): boolean {
  if (order.status === "CANCELLED") return true;
  return order.status === "COMPLETED" && !order.webAwaitingCourier;
}

/** The delivery job's status word, through the dictionary (see above). */
export function deliveryJobStatusLabel(t: TranslateFn, status: string): string {
  const known = [
    "PENDING",
    "ASSIGNED",
    "PICKED_UP",
    "DELIVERED",
    "FAILED",
    "CANCELLED",
  ];
  return known.includes(status)
    ? t(`deliveryStatus.${status}` as PlainKey)
    : status.toLowerCase();
}

export function deliveryJobStatusClass(status: string): string {
  switch (status) {
    case "PENDING":
      return "badge warning";
    case "ASSIGNED":
      return "badge primary";
    case "PICKED_UP":
      return "badge primary";
    case "DELIVERED":
      return "badge success";
    case "FAILED":
      return "badge danger";
    default:
      return "badge muted";
  }
}

/**
 * TAKES `t`, rather than importing the module one, and the difference is the
 * REPAINT and not the string.
 *
 * `useT` subscribes its component to the language store; the module `t` reads
 * the active language when called and tells React nothing. A helper using the
 * module `t` is therefore right on first paint and STALE after a language
 * switch, until that component re-renders for some other reason. Three of
 * this function's four callers do not call `useT` at all.
 *
 * Passing `t` in makes the subscription a compile error to forget: the caller
 * cannot supply it without having called `useT` first. Same shape as
 * `recurringRuleLabel(t, locale, schedule)` in `ShopFloorTasksPanel`.
 *
 * The key keeps the Prisma code verbatim (S3's rule) so the two cannot drift.
 */
export function fulfillmentLabel(
  t: TranslateFn,
  fulfillment: OrderFulfillment,
): string {
  return t(`fulfillment.${fulfillment}` as PlainKey);
}

/** "15:00" / "15:00–17:00" / "any time" for the order's slot. */
export function orderSlotLabel(t: TranslateFn, order: CustomerOrder): string {
  // S5: already 24-hour, but `undefined` let the DEVICE choose the digits
  // and separator. Now the florist's language does.
  const time = (iso: string) => timeText(new Date(iso));
  if (order.timeType === "WINDOW" && order.scheduledFor && order.scheduledUntil) {
    return `${time(order.scheduledFor)}–${time(order.scheduledUntil)}`;
  }
  if (order.scheduledFor) return time(order.scheduledFor);
  return t("orders.slotAnyTime");
}

export function orderLinesSummary(order: CustomerOrder): string {
  return order.lines
    .map((line) => {
      const qty = Number(line.quantity);
      const base = line.bouquetTemplateName ?? line.itemName ?? line.description;
      return qty !== 1 ? `${qty}× ${base}` : base;
    })
    .join(", ");
}

/** Sort for agenda display: timed orders by slot, ASAP orders first. */
export function compareOrdersForDisplay(a: CustomerOrder, b: CustomerOrder): number {
  const aTime = a.scheduledFor ? new Date(a.scheduledFor).getTime() : 0;
  const bTime = b.scheduledFor ? new Date(b.scheduledFor).getTime() : 0;
  if (aTime !== bTime) return aTime - bTime;
  // NOT compareText: these are ISO timestamps, not words a florist reads.
  // ASCII order is already chronological, and a locale collation has nothing
  // to add and could reorder ties.
  return a.createdAt.localeCompare(b.createdAt);
}
